import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { Kpis } from '@/components/Kpis'
import { useCursorPager } from '@/data/useCursorPager'
import { TRIGGER_LABEL, formatJobTime } from '@/screens/shared/jobFormatting'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestorePoint = components['schemas']['RestorePoint']
type ReportCatalogue = components['schemas']['ReportCatalogue']
type Report = components['schemas']['Report']
type Observation = components['schemas']['Observation']

function isObservation(value: unknown): value is Observation {
  return typeof value === 'object' && value !== null && 'state' in value
}

function renderCell(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (isObservation(value)) {
    if (value.state === 'OBSERVED') return String(value.value ?? '—')
    return value.display ?? value.state
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/**
 * `process/09-SCREEN-SPECS/_family-reports.md` — the shared engine for all 21 report screens
 * (`v-access`…`v-rnoauthor`), the same "config-driven, one component, N call sites" shape Pass 6's
 * `RestoreWizardLayer` established for the 8 restore machines. **`report_key` is opaque and served
 * only by the catalogue** (`process/06-REPORT-SCREEN-MAP.md`: "the key was recorded nowhere — not
 * on the unit, not in the contract, not in the prototype") — this component never constructs one;
 * it fetches `reportListCatalogue` for the chosen configuration snapshot and resolves `report_key`
 * by matching this report's own `title` (the map's own join key, "never the heading") against the
 * catalogue row.
 *
 * **`strip` added W158** — `Report.evidence_statement` (present on every person/permission report:
 * "evidence of absence in named signals, not evidence of inactivity") and `Report.baseline` (a
 * graded report's own grading rules, "published on the face of the report so nothing is hidden")
 * are both real, already-served fields this component fetched but never rendered; wrapped in the
 * shared `Strip` component rather than a plain `Note` now that both are drawn. **`kpi` added** —
 * `Report.kpis` (server-computed, server-labelled headline figures, §28.2) is rendered via the
 * shared `Kpis` component. **The period `segment-option`/`segmented-control` several reports'
 * specs also name is still NOT built** — confirmed against `reportGet` (only a snapshot-id
 * parameter, no period/threshold): the prototype's own `v-cicost`/`v-waste` render a bespoke
 * 30/90/365-day toggle that the generic `{columns, rows}` transport has no field for at all. A
 * contract gap, not a rendering one.
 */
export function ReportLayer({ unitId, title, notes }: { unitId: string; title: string; notes?: readonly string[] }) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const [manualJobId, setManualJobId] = useState('')
  const pager = useCursorPager()

  const points = useQuery({
    queryKey: ['config-restore-points', orgId, workspaceId],
    // Found live, 2026-09-25 (same bug/fix as VHealth.tsx/VDrift.tsx): with no guard, a mount-time
    // or org-switch race (`ShellLayer`'s own `setActiveOrg`/workspace-selection effects run after
    // the first render, `data/store.ts`) fired this with a null org/workspace id — openapi-fetch
    // leaves the unmatched `{org_id}`/`{workspace_id}` placeholder literally in the URL.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<readonly RestorePoint[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { kind: 'CONFIG_BACKUP', include_expired: true } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly RestorePoint[]
    },
  })

  const jobId = manualJobId || points.data?.[0]?.data_snapshot.job_id || ''

  const catalogue = useQuery({
    queryKey: ['report-catalogue', orgId, workspaceId, jobId],
    enabled: Boolean(jobId),
    queryFn: async (): Promise<ReportCatalogue> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/reports', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { snapshot_job_id: jobId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ReportCatalogue
    },
  })

  const catalogueRow = catalogue.data?.reports.find((r) => r.title === title)
  const reportKey = catalogueRow?.report_key
  const configSnapshot = catalogue.data?.snapshot.configuration_snapshot

  const report = useQuery({
    queryKey: ['report', orgId, workspaceId, reportKey, jobId, pager.cursor],
    enabled: Boolean(reportKey),
    queryFn: async (): Promise<Report> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/reports/{report_key}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, report_key: reportKey! }, query: { snapshot_job_id: jobId, cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Report
    },
  })

  const exportReport = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/reports/{report_key}/export', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, report_key: reportKey! }, query: { snapshot_job_id: jobId } },
        parseAs: 'blob',
      })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      const url = URL.createObjectURL(response.data as unknown as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${unitId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const problem = report.error instanceof ApiProblemError ? report.error.problem : catalogue.error instanceof ApiProblemError ? catalogue.error.problem : undefined

  return (
    <div data-scr={brandUnitId(unitId)} className="p-8">
      {/* `tools` added — `Prototype_v2.html`'s own simple reports (`v-radmins`/`v-runused`/…) draw
       * `<div class="panel-h"><h3>…</h3><div class="tools"><button>Export report</button></div></div>`
       * verbatim: the Export control's cluster is `tools` (`div.tools`, "a cluster of controls above
       * a surface"), a DIFFERENT catalogued name from `grid-tools` (`div.gridtools`, "the header-row
       * control cluster" the family baseline's own `grid-table` already requires) — not a variant
       * spelling of the same thing. Both are real here: the one control is simultaneously the
       * panel-header's own cluster and the grid's, so it is nested rather than duplicated into two
       * buttons. Several reports' own specs name `tools` as an addition "beyond the family set";
       * this was the missing element `l1_conform.py` found on all of them. */}
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">{title}</h1>
        <div data-cmp="tools">
          <div data-cmp="grid-tools">
            <Button data-act={actionId('report.export')} ghost disabled={!reportKey || exportReport.isPending} onClick={() => exportReport.mutate()}>
              Export report
            </Button>
          </div>
        </div>
      </div>

      <label className="block mb-6 text-small font-semibold text-tx2" style={{ maxWidth: 420 }}>
        Configuration snapshot
        <select
          value={jobId}
          onChange={(e) => {
            setManualJobId(e.target.value)
            pager.reset()
          }}
          className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg"
        >
          {(points.data ?? []).map((p) => (
            <option key={p.data_snapshot.job_id} value={p.data_snapshot.job_id}>
              {p.data_snapshot.handle} — {p.started_at ? p.started_at.display : p.data_snapshot.job_id}
            </option>
          ))}
        </select>
      </label>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {/* `Prototype_v2.html`'s own `.strip` above every one of the 21 report tables — served data
          only (`configuration_snapshot`'s own `started_at`/`trigger`, `catalogueRow.category`),
          added per the visual-diff sweep's semantic-content pass; unlike `kpi`/the period toggle
          (this file's own doc comment above), nothing here needed a field the contract lacks. */}
      {configSnapshot && catalogueRow && (
        <Strip>
          Computed from the configuration snapshot taken <span className="font-mono">{formatJobTime(configSnapshot.started_at)}</span> ·{' '}
          {TRIGGER_LABEL[configSnapshot.trigger] ?? configSnapshot.trigger}. No Bitbucket call is made.{' '}
          <span className="text-tx2">
            Category: {catalogueRow.category}. Columns are specified in design/28-report-catalogue.md §28.2.
          </span>
        </Strip>
      )}

      {report.data?.kpis && report.data.kpis.length > 0 && (
        <div className="mb-6">
          <Kpis items={report.data.kpis} />
        </div>
      )}

      {(report.data?.evidence_statement || (report.data?.baseline?.length ?? 0) > 0) && (
        <Strip>
          {report.data?.evidence_statement && <p className="m-0">{report.data.evidence_statement}</p>}
          {report.data?.baseline && report.data.baseline.length > 0 && (
            <ul className="m-0 mt-2 pl-6">
              {report.data.baseline.map((rule, i) => (
                <li key={i}>{rule}</li>
              ))}
            </ul>
          )}
        </Strip>
      )}

      {/* Static per-report methodology copy, verbatim from `Prototype_v2.html`'s own `.note` for
          this screen — not served by `Report` (no such field exists on the contract), so this is
          hand-transcribed structural copy rather than a rendered value, the same class of fix as a
          heading or a column label. */}
      {notes?.map((n, i) => (
        <div key={i} className="mt-4">
          <Note>{n}</Note>
        </div>
      ))}

      <div className="mt-4">
        <GridTable
          columns={(report.data?.columns ?? []).map((c) => ({
            key: c.key,
            header: c.label,
            render: (item: { row: Record<string, unknown>; i: number }) => renderCell(item.row[c.key]),
          }))}
          rows={(report.data?.rows ?? []).map((row, i) => ({ row: row as Record<string, unknown>, i }))}
          getRowKey={(item) => String(item.i)}
          emptyMessage={report.data ? 'No rows match this report for the snapshot selected.' : ''}
        />
      </div>

      <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between mt-5">
        <Note>Paged by cursor only — no total is served for this report.</Note>
        <Pager
          hasPrev={pager.hasPrev}
          hasNext={Boolean(report.data?.next_cursor)}
          loading={report.isFetching}
          onPrev={pager.goPrev}
          onNext={() => {
            if (report.data?.next_cursor) pager.goNext(report.data.next_cursor)
          }}
        />
      </div>
    </div>
  )
}
